const DAY = 86400;

function mean(a) {
  return a.length ? a.reduce((x, y) => x + y, 0) / a.length : NaN;
}
function std(a) {
  if (a.length < 2) return NaN;
  const m = mean(a);
  return Math.sqrt(a.reduce((x, y) => x + (y - m) ** 2, 0) / (a.length - 1));
}

/** Last equity of every UTC day: [{day, time, equity}] */
export function dailyEquity(times, equity) {
  const out = [];
  for (let i = 0; i < times.length; i += 1) {
    const day = Math.floor(times[i] / DAY);
    if (out.length && out[out.length - 1].day === day) {
      out[out.length - 1].equity = equity[i];
      out[out.length - 1].time = times[i];
    } else out.push({ day, time: times[i], equity: equity[i] });
  }
  return out;
}

export function dailyReturns(times, equity, startEquity) {
  const d = dailyEquity(times, equity);
  const rets = [];
  let prev = startEquity;
  d.forEach((p) => {
    rets.push({ day: p.day, r: prev > 0 ? p.equity / prev - 1 : 0 });
    prev = p.equity;
  });
  return rets;
}

export function drawdownSeries(equity) {
  let peak = -Infinity;
  return equity.map((v) => {
    if (v > peak) peak = v;
    return peak > 0 ? v / peak - 1 : 0;
  });
}

export function monthlyReturns(times, equity, startEquity) {
  const months = new Map();
  let prev = startEquity;
  for (let i = 0; i < times.length; i += 1) {
    const dt = new Date(times[i] * 1000);
    const key = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`;
    if (!months.has(key)) months.set(key, { start: prev, end: equity[i] });
    months.get(key).end = equity[i];
    prev = equity[i];
  }
  return [...months.entries()].map(([key, v]) => ({ key, ret: v.start > 0 ? v.end / v.start - 1 : 0 }));
}

/** Trailing calendar-window returns, drawdowns, volatility, and Sharpe from daily closes. */
export function rollingPerformance(times, equity, startEquity, windows = [30, 90, 180]) {
  const days = dailyEquity(times, equity);
  if (!days.length) return [];
  const lastDay = days[days.length - 1].day;
  return windows.map((windowDays) => {
    const boundary = lastDay - Math.max(1, Math.floor(windowDays)) + 1;
    const firstIndex = Math.max(0, days.findIndex((point) => point.day >= boundary));
    const baseline = firstIndex > 0 ? days[firstIndex - 1].equity : startEquity;
    let previous = baseline;
    let peak = baseline;
    let maxDrawdown = 0;
    const returns = [];
    for (let index = firstIndex; index < days.length; index += 1) {
      const point = days[index];
      const dailyReturn = previous > 0 ? point.equity / previous - 1 : 0;
      returns.push(dailyReturn);
      previous = point.equity;
      peak = Math.max(peak, point.equity);
      if (peak > 0) maxDrawdown = Math.min(maxDrawdown, point.equity / peak - 1);
    }
    const deviation = std(returns);
    const annualizer = Math.sqrt(365);
    return {
      windowDays,
      observations: returns.length,
      actualDays: Math.max(1, lastDay - (days[firstIndex]?.day ?? lastDay) + 1),
      totalReturn: baseline > 0 ? days[days.length - 1].equity / baseline - 1 : NaN,
      maxDrawdown,
      annualizedVolatility: Number.isFinite(deviation) ? deviation * annualizer : NaN,
      sharpe: Number.isFinite(deviation) && deviation > 0 ? (mean(returns) / deviation) * annualizer : NaN,
    };
  });
}

/** All the headline numbers for one equity curve. */
export function computeMetrics({ times, equity, position, trades }, capital) {
  const n = equity.length;
  if (n < 2) return null;
  const endEq = equity[n - 1];
  const totalReturn = endEq / capital - 1;
  const years = Math.max((times[n - 1] - times[0]) / (365.25 * DAY), 1 / 365.25);
  const cagr = endEq > 0 ? (endEq / capital) ** (1 / years) - 1 : -1;

  const dd = drawdownSeries(equity);
  let maxDD = 0;
  let ddEnd = 0;
  dd.forEach((v, i) => {
    if (v < maxDD) {
      maxDD = v;
      ddEnd = i;
    }
  });
  // longest time under water, in days
  let longest = 0;
  let startUW = null;
  for (let i = 0; i < n; i += 1) {
    if (dd[i] < 0) {
      if (startUW === null) startUW = times[i];
      longest = Math.max(longest, times[i] - startUW);
    } else startUW = null;
  }

  const daily = dailyReturns(times, equity, capital).map((x) => x.r);
  const annFactor = Math.sqrt(Math.max(daily.length / years, 1));
  const vol = std(daily) * annFactor;
  const downside = Math.sqrt(mean(daily.map((r) => Math.min(r, 0) ** 2)));
  const sharpe = std(daily) > 0 ? (mean(daily) / std(daily)) * annFactor : NaN;
  const sortino = downside > 0 ? (mean(daily) / downside) * annFactor : NaN;
  const calmar = maxDD < 0 ? cagr / Math.abs(maxDD) : NaN;

  const t = trades || [];
  const wins = t.filter((x) => x.pnl > 0);
  const losses = t.filter((x) => x.pnl <= 0);
  const grossWin = wins.reduce((a, x) => a + x.pnl, 0);
  const grossLoss = Math.abs(losses.reduce((a, x) => a + x.pnl, 0));
  let maxWinStreak = 0;
  let maxLossStreak = 0;
  let w = 0;
  let l = 0;
  t.forEach((x) => {
    if (x.pnl > 0) {
      w += 1;
      l = 0;
    } else {
      l += 1;
      w = 0;
    }
    maxWinStreak = Math.max(maxWinStreak, w);
    maxLossStreak = Math.max(maxLossStreak, l);
  });
  const avgWin = wins.length ? mean(wins.map((x) => x.ret)) : NaN;
  const avgLoss = losses.length ? mean(losses.map((x) => x.ret)) : NaN;
  const rs = t.filter((x) => Number.isFinite(x.rMultiple)).map((x) => x.rMultiple);
  const reasons = {};
  t.forEach((x) => {
    const r = reasons[x.reason] || (reasons[x.reason] = { count: 0, ret: 0 });
    r.count += 1;
    r.ret += x.ret;
  });
  const exposure = position && position.length ? mean(position.map((p) => Math.abs(p))) : NaN;

  return {
    startEquity: capital,
    endEquity: endEq,
    totalReturn,
    cagr,
    years,
    sharpe,
    sortino,
    calmar,
    volatility: vol,
    maxDrawdown: maxDD,
    maxDrawdownTime: times[ddEnd],
    longestDrawdownDays: longest / DAY,
    recoveryFactor: maxDD < 0 ? totalReturn / Math.abs(maxDD) : NaN,
    trades: t.length,
    longTrades: t.filter((x) => x.side === 1).length,
    shortTrades: t.filter((x) => x.side === -1).length,
    winRate: t.length ? wins.length / t.length : NaN,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : NaN,
    avgWin,
    avgLoss,
    payoff: Number.isFinite(avgWin) && Number.isFinite(avgLoss) && avgLoss !== 0 ? avgWin / Math.abs(avgLoss) : NaN,
    expectancy: t.length ? mean(t.map((x) => x.ret)) : NaN,
    bestTrade: t.length ? Math.max(...t.map((x) => x.ret)) : NaN,
    worstTrade: t.length ? Math.min(...t.map((x) => x.ret)) : NaN,
    avgBars: t.length ? mean(t.map((x) => x.bars)) : NaN,
    maxWinStreak,
    maxLossStreak,
    exposure,
    avgR: rs.length ? mean(rs) : NaN, // average result in R (multiples of the initial risk), needs a stop loss
    bestR: rs.length ? Math.max(...rs) : NaN,
    worstR: rs.length ? Math.min(...rs) : NaN,
    reasons, // {stop:{count,ret}, target:{...}, signal:{...}, end:{...}}
  };
}

export function correlationMatrix(runs) {
  const series = runs.map((r) => dailyReturns(r.times, r.equity, r.equity[0]).map((x) => x.r));
  const corr = (a, b) => {
    const m = Math.min(a.length, b.length);
    const x = a.slice(0, m);
    const y = b.slice(0, m);
    const mx = mean(x);
    const my = mean(y);
    let sxy = 0;
    let sxx = 0;
    let syy = 0;
    for (let i = 0; i < m; i += 1) {
      sxy += (x[i] - mx) * (y[i] - my);
      sxx += (x[i] - mx) ** 2;
      syy += (y[i] - my) ** 2;
    }
    return sxx > 0 && syy > 0 ? sxy / Math.sqrt(sxx * syy) : NaN;
  };
  return series.map((a) => series.map((b) => corr(a, b)));
}

/** Histogram of trade returns (in %) with `bins` equal buckets. */
export function histogram(values, bins = 20) {
  if (!values.length) return [];
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const width = hi === lo ? 1 : (hi - lo) / bins;
  const out = Array.from({ length: bins }, (_, i) => ({ from: lo + i * width, to: lo + (i + 1) * width, count: 0 }));
  values.forEach((v) => {
    const idx = Math.min(bins - 1, Math.floor((v - lo) / width));
    out[idx].count += 1;
  });
  return out;
}
